/**
 * Blog Listing Page
 * SEO-optimized with scheduled publishing from Supabase
 */

import { useState, useEffect } from 'react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Link } from 'wouter';
import { Search, Clock, ArrowRight, Tag, Loader2, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { trackBlogCategoryFilter, trackBlogSearch } from '@/lib/tracking-helpers';
import { SEO } from '@/components/SEO';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  author_name: string;
  publish_at: string;
  read_time_minutes: number;
  category: string;
  tags: string[];
  featured_image: string;
}

const categories = ['All', 'LinkedIn', 'Lead Generation', 'Cold Outreach', 'Sales Tips', 'Automation', 'Productivity'];

// Category icons/emojis
const categoryEmojis: Record<string, string> = {
  'LinkedIn': '💼',
  'Lead Generation': '🎯',
  'Cold Outreach': '📧',
  'Sales Tips': '💡',
  'Automation': '🤖',
  'Productivity': '⚡',
  'default': '📝'
};

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Handle category filter
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (category !== 'All') {
      trackBlogCategoryFilter(category);
    }
  };

  // Handle search with debouncing
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    // Track search after user stops typing (debounced)
    if (query.length >= 3) {
      const timeoutId = setTimeout(() => {
        trackBlogSearch(query);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, slug, title, excerpt, author_name, publish_at, read_time_minutes, category, tags, featured_image')
        .eq('status', 'published')
        .lte('publish_at', new Date().toISOString())
        .order('publish_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Error loading posts:', err);
      // Fallback to empty - migration might not be run yet
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter posts by category and search
  const filteredPosts = posts.filter((post) => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  const getEmoji = (category: string) => categoryEmojis[category] || categoryEmojis['default'];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <MarketingLayout>
      <SEO
        title="Blog | Prozpector"
        description="Read the latest cold prospecting tips, sales strategies, and industry insights from the Prozpector team."
        url="https://prozpector.com/blog"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-32">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              The Prozpector Blog
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
              Proven strategies to automate your sales prospecting, generate more leads, and close more deals.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  className="pl-12 h-14 text-lg"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b">
        <div className="container">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCategoryChange(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Loading State */}
      {isLoading && (
        <section className="py-20">
          <div className="container">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-gray-600">Loading articles...</p>
            </div>
          </div>
        </section>
      )}

      {/* No Posts State */}
      {!isLoading && posts.length === 0 && (
        <section className="py-20">
          <div className="container">
            <div className="max-w-2xl mx-auto text-center">
              <div className="text-6xl mb-6">📝</div>
              <h2 className="text-2xl font-bold mb-4">Blog Coming Soon</h2>
              <p className="text-gray-600 mb-8">
                We're working on creating valuable content for you. Check back soon for articles on sales automation, lead generation, and B2B prospecting strategies.
              </p>
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg">
                  Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Featured Post */}
      {!isLoading && featuredPost && (
        <section className="py-16 bg-white">
          <div className="container">
            <div className="max-w-5xl mx-auto">
              <div className="mb-8">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-black mb-4">
                  <Tag className="h-4 w-4" />
                  Featured Article
                </span>
              </div>
              <Card className="overflow-hidden hover:shadow-xl transition-shadow">
                <Link href={`/blog/${featuredPost.slug}`}>
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-8xl p-12">
                      {getEmoji(featuredPost.category)}
                    </div>
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <Badge className="bg-black text-white">
                          {featuredPost.category}
                        </Badge>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {featuredPost.read_time_minutes} min read
                        </span>
                      </div>
                      <h2 className="text-3xl font-bold mb-4">{featuredPost.title}</h2>
                      <p className="text-gray-600 mb-6 text-lg">{featuredPost.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(featuredPost.publish_at)}
                        </span>
                        <Button>
                          Read Article <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      {!isLoading && remainingPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Latest Articles</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {remainingPosts.map((post) => (
                  <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                    <Link href={`/blog/${post.slug}`}>
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-6xl p-12">
                        {getEmoji(post.category)}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                          <Badge className="bg-black text-white">
                            {post.category}
                          </Badge>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {post.read_time_minutes} min read
                          </span>
                        </div>
                        <h3 className="text-xl font-bold mb-3 group-hover:text-gray-600 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500 flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(post.publish_at)}
                          </span>
                          <span className="font-medium text-black flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read more <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Beta Program CTA */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto bg-black text-white rounded-2xl p-8 md:p-12 text-center">
            <div className="inline-block bg-primary/20 text-primary-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Done-For-You Outbound
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Fill Your Calendar?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let us handle your outbound so you can focus on closing deals.
            </p>
            <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="bg-white text-black hover:bg-gray-100 border-white">
                Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <p className="text-sm text-gray-400 mt-4">
              Qualified meetings booked directly on your calendar
            </p>
          </div>
        </div>
      </section>

      {/* Popular Topics */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Popular Topics</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card
                className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleCategoryChange('LinkedIn')}
              >
                <div className="text-4xl mb-4">💼</div>
                <h3 className="text-xl font-bold mb-2">LinkedIn Automation</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Stop posting manually. Learn how to automate your LinkedIn presence.
                </p>
                <span className="text-sm font-medium text-black flex items-center justify-center gap-1">
                  View articles <ArrowRight className="h-4 w-4" />
                </span>
              </Card>

              <Card
                className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleCategoryChange('Cold Outreach')}
              >
                <div className="text-4xl mb-4">📧</div>
                <h3 className="text-xl font-bold mb-2">Cold Outreach</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Stop manual prospecting. Automate your outreach sequences.
                </p>
                <span className="text-sm font-medium text-black flex items-center justify-center gap-1">
                  View articles <ArrowRight className="h-4 w-4" />
                </span>
              </Card>

              <Card
                className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleCategoryChange('Lead Generation')}
              >
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-xl font-bold mb-2">Lead Generation</h3>
                <p className="text-gray-600 text-sm mb-4">
                  We fill your pipeline with qualified leads while you focus on closing.
                </p>
                <span className="text-sm font-medium text-black flex items-center justify-center gap-1">
                  View articles <ArrowRight className="h-4 w-4" />
                </span>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Done-For-You SDR Service
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Stop Reading. Start Booking.
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              We handle the entire outbound process - from prospect research to meeting booking. You just show up and close.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="text-lg px-8 py-6">
                  Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  Get a Quote
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
